import { Flex, Select as AntSelect, SelectProps as AntSelectProps } from 'antd';
import React, { useMemo } from 'react';
import styled from 'styled-components';
import { Approve24 } from '../../assets';
import { IconWrap } from '../../icons/icon';
import { theme } from '../../theme/semanticTokens';
import { IconSize } from '../../types/icons/Icon.types';
import { IconType } from '../../types/icons/IconTypes.enum';
import { Typography } from '../../Typography';

export enum SelectMode {
  Single = 'single',
  Multiple = 'multiple'
}

export interface SelectOption<T = string | number> {
  label: React.ReactNode;
  title?: React.ReactNode;
  value: T;
  disabled?: boolean;
}

export interface SelectDivider {
  type: 'divider';
  key?: string;
}

export type SelectItem<T = string | number> = SelectOption<T> | SelectDivider;

export type SelectDimeProps<T = string | number> = Omit<
  AntSelectProps<T>,
  'mode' | 'options'
> & {
  helpText?: string;
  mode?: SelectMode;
  options: SelectItem<T>[];
  value?: T | T[];
  defaultValue?: T | T[];
  keepEnabledWhenEmpty?: boolean;
};

const ForwardedAntSelect = React.forwardRef<
  any,
  AntSelectProps<any> & { $mode?: SelectMode }
>(({ $mode, ...props }, ref) => <AntSelect ref={ref} {...props} />);

const StyledSelect = styled(ForwardedAntSelect)<{ $mode?: SelectMode }>`
  width: 100%;

  .ant-select-selector {
    border-radius: ${theme.dime.radius.m};
    border: 1px solid ${theme.dime.colors.border.static};
    background-color: ${theme.dime.colors.surface.primary};
    padding: ${theme.dime.spacing.m} ${theme.dime.spacing.l};
    height: 40px;
    min-height: 40px;
    align-content: center;
    align-items: center;
    font-family: ${theme.dime.fonts.families.roboto};
    font-size: ${theme.dime.fonts.sizes.sm};
    font-weight: ${theme.dime.fonts.weights.regular};
    line-height: ${theme.dime.fonts.lineHeights.sm};

    &:hover {
      border-color: ${theme.dime.colors.border.interactiveHover};
    }

    &:focus-within {
      border-color: ${theme.dime.colors.border.interactiveSelected};
      box-shadow: 0 0 0 2px ${theme.dime.colors.surface.primarySelected};
    }
  }

  &.error .ant-select-selector {
    border-color: ${theme.dime.colors.border.critical};

    &:hover {
      border-color: ${theme.dime.colors.text.critical};
    }

    &:focus-within {
      border-color: ${theme.dime.colors.border.critical};
      box-shadow: 0 0 0 2px ${theme.dime.colors.surface.primary};
    }
  }

  &.ant-select-disabled .ant-select-selector {
    background-color: ${theme.dime.colors.surface.primaryDisabled};
    border-color: ${theme.dime.colors.border.interactiveDisabled};
    color: ${theme.dime.colors.text.disabled};
  }

  .ant-select-selection-placeholder {
    color: ${theme.dime.colors.text.secondary};
  }

  .ant-select-arrow {
    color: ${theme.dime.colors.icon.primary};
    right: ${theme.dime.spacing.l};

    ${({ $mode }) =>
      $mode === SelectMode.Single
        ? `
          align-self: anchor-center;
    `
        : ''}
  }

  .ant-select-clear {
    right: 18px;
    ${({ $mode }) =>
      $mode === SelectMode.Single
        ? `
          align-self: anchor-center;
    `
        : ''}
  }

  &.error .ant-select-arrow {
    color: ${theme.dime.colors.text.critical};
  }

  &.ant-select-open .ant-select-selector {
    border-color: ${theme.dime.colors.border.interactiveSelected};
    box-shadow: 0 0 0 2px ${theme.dime.colors.surface.primarySelected};
  }

  &.error.ant-select-open .ant-select-selector {
    border-color: ${theme.dime.colors.text.critical};
    box-shadow: 0 0 0 2px ${theme.dime.colors.surface.primary};
  }
`;

const StyledOptionWrapper = styled(Flex)`
  width: 100%;
`;

const StyledDivider = styled.div`
  height: 1px;
  background-color: ${theme.dime.colors.border.static};
  margin-bottom: ${theme.dime.spacing.m};
  margin-top: ${theme.dime.spacing.s};
  width: 100%;
  pointer-events: none;
`;

const StyledApproveIcon = styled(Approve24)<{
  $isSelected: boolean;
  $isDisabled?: boolean;
}>`
  opacity: ${({ $isSelected }) => ($isSelected ? 1 : 0.2)};
  transition: opacity 0.2s ease;
  color: ${({ $isDisabled }) =>
    $isDisabled
      ? theme.dime.colors.icon.secondary
      : theme.dime.colors.icon.link};
`;

const StyledDropdown = styled.div<{ $mode?: SelectMode }>`
  .ant-select-dropdown {
    background: ${theme.dime.colors.surface.primary};
    border-radius: ${theme.dime.radius.m};
    border: none;
    box-shadow: 0px 0px 14px 0px rgba(0, 0, 0, 0.08);
    padding: ${theme.dime.spacing.s};

    .ant-select-item {
      border-radius: ${theme.dime.radius.m};
      padding: ${theme.dime.spacing.m} ${theme.dime.spacing.l};
      margin: 0 0 ${theme.dime.spacing.s} 0;
      font-family: ${theme.dime.fonts.families.roboto};
      font-size: ${theme.dime.fonts.sizes.sm};
      font-weight: ${theme.dime.fonts.weights.regular};
      line-height: ${theme.dime.fonts.lineHeights.sm};

      &:last-child {
        margin-bottom: 0;
      }

      /* Style divider options */
      &.select-divider {
        padding: 0;
        margin: 0;
        cursor: default !important;
        height: calc(
          1px + 2 * ${theme.dime.spacing.m}
        ); /* 1px line + medium spacing top/bottom */
        min-height: calc(1px + 2 * ${theme.dime.spacing.m});
      }

      /* Hover effect for approve icon - only for multi-select */
      ${({ $mode }) =>
        $mode === SelectMode.Multiple &&
        `
        &:hover:not(.ant-select-item-option-disabled) {
          svg {
            opacity: 1 !important;
          }
        }
      `}

      &.ant-select-item-option-active {
        background-color: ${theme.dime.colors.surface.secondaryHover};
      }

      &.ant-select-item-option-selected {
        background-color: ${theme.dime.colors.surface.primarySelected};
      }

      &.ant-select-item-option-disabled {
        color: ${theme.dime.colors.text.disabled};
      }
    }
  }
`;

const SelectWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.dime.spacing.s};
  height: 40px;
`;

const HelpText = styled(Typography)<{ $hasError: boolean }>`
  color: ${({ $hasError }) =>
    $hasError
      ? theme.dime.colors.text.critical
      : theme.dime.colors.text.secondary};
  margin-top: ${theme.dime.spacing.m};
`;

const isDivider = <T extends string | number>(
  item: SelectItem<T>
): item is SelectDivider => {
  return 'type' in item && item.type === 'divider';
};

const SelectInner = <T extends string | number = string | number>(
  {
    options,
    mode = SelectMode.Single,
    placeholder,
    disabled = false,
    className,
    allowClear = true,
    status = '',
    helpText,
    keepEnabledWhenEmpty = false,
    ...props
  }: SelectDimeProps<T>,
  ref: React.ForwardedRef<any>
) => {
  const isMultipleMode = mode === SelectMode.Multiple;

  const placeholderText =
    placeholder || (isMultipleMode ? 'Select options...' : 'Select an option');

  const antOptions = useMemo(
    () =>
      options!.map((item, index) => {
        if (isDivider(item)) {
          return {
            label: <StyledDivider />,
            title: '',
            value: `__divider_${index}__`,
            disabled: true,
            className: 'select-divider'
          };
        }

        const option = item as SelectOption;
        const isSelected = isMultipleMode
          ? Array.isArray(props.value) &&
            props.value.includes(option.value as T)
          : props.value === option.value;

        const dropdownLabel = isMultipleMode ? (
          <StyledOptionWrapper align="center" justify="space-between">
            <Typography variant="body1">{option.label}</Typography>
            <StyledApproveIcon
              $isSelected={isSelected}
              $isDisabled={option.disabled}
            />
          </StyledOptionWrapper>
        ) : (
          <Typography variant="body1">{option.label}</Typography>
        );

        return {
          ...option,
          label: dropdownLabel,
          title: option.title || option.label
        };
      }),
    [options, isMultipleMode, props.value]
  );

  const antMode = isMultipleMode ? SelectMode.Multiple : undefined;
  const hasError = status === 'error';
  const combinedClassName = `${status} ${className || ''}`;

  return (
    <SelectWrapper>
      <StyledDropdown $mode={mode}>
        <StyledSelect
          className={combinedClassName}
          options={antOptions}
          mode={antMode}
          $mode={mode}
          placeholder={placeholderText}
          disabled={disabled || (!keepEnabledWhenEmpty && options.length === 0)}
          allowClear={
            !!allowClear && {
              clearIcon: (
                <IconWrap size={IconSize.Small} type={IconType.Close} />
              )
            }
          }
          suffixIcon={
            <IconWrap size={IconSize.Small} type={IconType.ChevronDown} />
          }
          menuItemSelectedIcon={null}
          optionLabelProp={'title'}
          getPopupContainer={(trigger) =>
            trigger.parentElement || document.body
          }
          maxTagCount="responsive"
          ref={ref}
          {...props}
        />
      </StyledDropdown>
      {!!helpText && (
        <HelpText variant="body1" $hasError={hasError}>
          {helpText}
        </HelpText>
      )}
    </SelectWrapper>
  );
};

type SelectComponent = <T extends string | number = string | number>(
  props: SelectDimeProps<T> & { ref?: React.Ref<any> }
) => JSX.Element;

export const Select = React.forwardRef(SelectInner) as SelectComponent;
